import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  ReactNode,
} from 'react';
import { playgroundApi } from '@/services/playgroundApi';
import { contentApi } from '@/services/contentApi';
import { useGlobalAssistant } from '@/contexts/GlobalAssistantContext';
import {
  HITLRequest,
  fetchHITLRequests,
  respondToHITLRequest,
  rejectHITLRequest,
} from '@/services/hitlApi';

// ============================================================================
// Types
// ============================================================================

export interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  toolCalls?: any[];
  isStreaming?: boolean;
}

export interface TraceEvent {
  id: string;
  type: string;
  name?: string;
  timestamp: string;
  data?: any;
}

export interface PromptConfig {
  mode: 'append' | 'override' | 'variable';
  targetVariable?: string;
}

interface IDEState {
  // Execution mode
  mode: 'oneshot' | 'chat';

  // Execution state
  isRunning: boolean;
  trajectoryId: string | null;
  status: 'idle' | 'running' | 'completed' | 'failed' | 'cancelled' | 'hitl_pending';

  // One-shot mode
  output: string | null;
  runError: string | null;
  userPrompt: string;
  promptConfig: PromptConfig;
  inputVariables: Record<string, string>;

  // Chat mode
  messages: Message[];

  // Shared execution data
  logs: string;
  traceEvents: TraceEvent[];

  // HITL state
  pendingHITLRequest: HITLRequest | null;

  // Panel state
  bottomPanelHeight: number; // 0 = collapsed
  bottomPanelTab: 'output' | 'logs' | 'trace';
  chatPanelOpen: boolean;
  chatPanelWidth: number;
}

// ============================================================================
// Actions
// ============================================================================

type Action =
  | { type: 'SET_MODE'; payload: 'oneshot' | 'chat' }
  | { type: 'SET_RUNNING'; payload: boolean }
  | { type: 'SET_TRAJECTORY_ID'; payload: string | null }
  | { type: 'SET_STATUS'; payload: 'idle' | 'running' | 'completed' | 'failed' | 'hitl_pending' }
  | { type: 'SET_OUTPUT'; payload: string | null }
  | { type: 'SET_RUN_ERROR'; payload: string | null }
  | { type: 'SET_USER_PROMPT'; payload: string }
  | { type: 'SET_PROMPT_CONFIG'; payload: PromptConfig }
  | { type: 'SET_INPUT_VARIABLES'; payload: Record<string, string> }
  | { type: 'SET_MESSAGES'; payload: Message[] }
  | { type: 'ADD_MESSAGE'; payload: Message }
  | { type: 'UPDATE_MESSAGE'; payload: { id: string; content: string; isStreaming?: boolean } }
  | { type: 'SET_LOGS'; payload: string }
  | { type: 'SET_TRACE_EVENTS'; payload: TraceEvent[] }
  | { type: 'SET_PENDING_HITL_REQUEST'; payload: HITLRequest | null }
  | { type: 'SET_BOTTOM_PANEL_HEIGHT'; payload: number }
  | { type: 'SET_BOTTOM_PANEL_TAB'; payload: 'output' | 'logs' | 'trace' }
  | { type: 'SET_CHAT_PANEL_OPEN'; payload: boolean }
  | { type: 'SET_CHAT_PANEL_WIDTH'; payload: number }
  | { type: 'RESET_EXECUTION' };

// ============================================================================
// Initial State
// ============================================================================

const initialState: IDEState = {
  mode: 'oneshot',
  isRunning: false,
  trajectoryId: null,
  status: 'idle',
  output: null,
  runError: null,
  userPrompt: '',
  promptConfig: { mode: 'append' },
  inputVariables: {},
  messages: [],
  logs: '',
  traceEvents: [],
  pendingHITLRequest: null,
  bottomPanelHeight: 0, // Collapsed by default
  bottomPanelTab: 'output',
  chatPanelOpen: false,
  chatPanelWidth: 350,
};

// ============================================================================
// Reducer
// ============================================================================

function reducer(state: IDEState, action: Action): IDEState {
  switch (action.type) {
    case 'SET_MODE':
      return { ...state, mode: action.payload };
    case 'SET_RUNNING':
      return { ...state, isRunning: action.payload };
    case 'SET_TRAJECTORY_ID':
      return { ...state, trajectoryId: action.payload };
    case 'SET_STATUS':
      return { ...state, status: action.payload };
    case 'SET_OUTPUT':
      return { ...state, output: action.payload };
    case 'SET_RUN_ERROR':
      return { ...state, runError: action.payload };
    case 'SET_USER_PROMPT':
      return { ...state, userPrompt: action.payload };
    case 'SET_PROMPT_CONFIG':
      return { ...state, promptConfig: action.payload };
    case 'SET_INPUT_VARIABLES':
      return { ...state, inputVariables: action.payload };
    case 'SET_MESSAGES':
      return { ...state, messages: action.payload };
    case 'ADD_MESSAGE':
      return { ...state, messages: [...state.messages, action.payload] };
    case 'UPDATE_MESSAGE':
      return {
        ...state,
        messages: state.messages.map((m) =>
          m.id === action.payload.id
            ? { ...m, content: action.payload.content, isStreaming: action.payload.isStreaming }
            : m
        ),
      };
    case 'SET_LOGS':
      return { ...state, logs: action.payload };
    case 'SET_TRACE_EVENTS':
      return { ...state, traceEvents: action.payload };
    case 'SET_PENDING_HITL_REQUEST':
      return { ...state, pendingHITLRequest: action.payload };
    case 'SET_BOTTOM_PANEL_HEIGHT':
      return { ...state, bottomPanelHeight: action.payload };
    case 'SET_BOTTOM_PANEL_TAB':
      return { ...state, bottomPanelTab: action.payload };
    case 'SET_CHAT_PANEL_OPEN':
      return { ...state, chatPanelOpen: action.payload };
    case 'SET_CHAT_PANEL_WIDTH':
      return { ...state, chatPanelWidth: action.payload };
    case 'RESET_EXECUTION':
      return {
        ...state,
        isRunning: false,
        trajectoryId: null,
        status: 'idle',
        output: null,
        runError: null,
        logs: '',
        traceEvents: [],
        pendingHITLRequest: null,
      };
    default:
      return state;
  }
}

// ============================================================================
// Context
// ============================================================================

interface IDEContextValue extends IDEState {
  // Mode actions
  setMode: (mode: 'oneshot' | 'chat') => void;

  // Execution actions
  runAgent: (agentId: string, agentContent: string) => Promise<void>;
  stopExecution: () => Promise<void>;
  sendChatMessage: (agentId: string, agentContent: string, message: string) => void;
  clearChat: () => void;

  // Input actions
  setUserPrompt: (prompt: string) => void;
  setPromptConfig: (config: PromptConfig) => void;
  setInputVariables: (variables: Record<string, string>) => void;

  // HITL actions
  approveHITL: (response?: Record<string, unknown>, notes?: string) => Promise<void>;
  rejectHITL: (reason?: string) => Promise<void>;
  dismissHITL: () => void;

  // Panel actions
  setBottomPanelHeight: (height: number) => void;
  setBottomPanelTab: (tab: 'output' | 'logs' | 'trace') => void;
  setChatPanelOpen: (open: boolean) => void;
  setChatPanelWidth: (width: number) => void;
  expandBottomPanel: () => void;
  collapseBottomPanel: () => void;

  // Reset
  resetExecution: () => void;
}

const IDEContext = createContext<IDEContextValue | null>(null);

// ============================================================================
// Provider
// ============================================================================

interface IDEProviderProps {
  children: ReactNode;
}

export function IDEProvider({ children }: IDEProviderProps) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { isOpen: isAssistantOpen, closePanel: closeAssistant } = useGlobalAssistant();

  // Refs for cleanup
  const pollStopRef = useRef<(() => void) | null>(null);
  const chatControllerRef = useRef<AbortController | null>(null);

  // Load persisted state on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem('ide_state');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.mode) dispatch({ type: 'SET_MODE', payload: parsed.mode });
        if (parsed.bottomPanelHeight !== undefined) {
          dispatch({ type: 'SET_BOTTOM_PANEL_HEIGHT', payload: parsed.bottomPanelHeight });
        }
        if (parsed.chatPanelWidth !== undefined) {
          dispatch({ type: 'SET_CHAT_PANEL_WIDTH', payload: parsed.chatPanelWidth });
        }
      }
    } catch (error) {
      console.error('Failed to restore IDE state:', error);
    }
  }, []);

  // Persist state changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      try {
        localStorage.setItem(
          'ide_state',
          JSON.stringify({
            mode: state.mode,
            bottomPanelHeight: state.bottomPanelHeight,
            chatPanelWidth: state.chatPanelWidth,
          })
        );
      } catch (error) {
        console.error('Failed to persist IDE state:', error);
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [state.mode, state.bottomPanelHeight, state.chatPanelWidth]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollStopRef.current) pollStopRef.current();
      if (chatControllerRef.current) chatControllerRef.current.abort();
    };
  }, []);

  // -------------------------------------------------------------------------
  // Actions
  // -------------------------------------------------------------------------

  const setMode = useCallback((mode: 'oneshot' | 'chat') => {
    dispatch({ type: 'SET_MODE', payload: mode });
    // Auto-open chat panel when switching to chat mode
    if (mode === 'chat') {
      dispatch({ type: 'SET_CHAT_PANEL_OPEN', payload: true });
      // Close global assistant when opening chat panel (mutual exclusivity)
      if (isAssistantOpen) {
        closeAssistant();
      }
    } else {
      dispatch({ type: 'SET_CHAT_PANEL_OPEN', payload: false });
    }
  }, [isAssistantOpen, closeAssistant]);

  const runAgent = useCallback(async (agentId: string, agentContent: string) => {
    if (state.isRunning) return;

    dispatch({ type: 'SET_RUNNING', payload: true });
    dispatch({ type: 'SET_STATUS', payload: 'running' });
    dispatch({ type: 'SET_OUTPUT', payload: null });
    dispatch({ type: 'SET_RUN_ERROR', payload: null });
    dispatch({ type: 'SET_LOGS', payload: '' });
    dispatch({ type: 'SET_TRACE_EVENTS', payload: [] });

    // Auto-expand bottom panel
    if (state.bottomPanelHeight === 0) {
      dispatch({ type: 'SET_BOTTOM_PANEL_HEIGHT', payload: 200 });
    }
    dispatch({ type: 'SET_BOTTOM_PANEL_TAB', payload: 'output' });

    try {
      // Apply prompt mode to determine how content/variables are sent
      let finalContent = agentContent;
      let finalVariables = { ...state.inputVariables };
      let finalUserPrompt = state.userPrompt;

      if (state.promptConfig.mode === 'override' && state.userPrompt) {
        // Replace the prompt field in frontmatter
        const frontmatterMatch = agentContent.match(/^---\n([\s\S]*?)\n---\n?([\s\S]*)$/);
        if (frontmatterMatch) {
          let newFrontmatter = frontmatterMatch[1];
          if (/^prompt:/m.test(newFrontmatter)) {
            newFrontmatter = newFrontmatter.replace(
              /^prompt:.*(?:\n(?:  |\t).*)*$/m,
              `prompt: |\n  ${state.userPrompt.replace(/\n/g, '\n  ')}`
            );
          } else {
            newFrontmatter = `${newFrontmatter}\nprompt: |\n  ${state.userPrompt.replace(/\n/g, '\n  ')}`;
          }
          finalContent = `---\n${newFrontmatter}\n---\n`;
          finalUserPrompt = '';
        }
      } else if (state.promptConfig.mode === 'variable' && state.promptConfig.targetVariable && state.userPrompt) {
        finalVariables[state.promptConfig.targetVariable] = state.userPrompt;
        finalUserPrompt = '';
      }

      const result = await playgroundApi.run({
        agent_id: agentId,
        input_variables: finalVariables,
        user_prompt: finalUserPrompt,
        agent_content: finalContent,
      });

      dispatch({ type: 'SET_TRAJECTORY_ID', payload: result.trajectory_id });

      // Start polling (2 second interval, auto-stops on completion)
      pollStopRef.current = playgroundApi.poll(
        result.trajectory_id,
        async (data) => {
          dispatch({ type: 'SET_LOGS', payload: data.logs });

          const mappedEvents = (data.events || []).map((e: any) => ({
            id: e._id || e.event_id || e.id,
            type: e.event_type || e.type,
            name: e.data?.tool_name || e.data?.agent_name || e.name,
            timestamp: e.timestamp,
            data: e.data,
          }));
          dispatch({ type: 'SET_TRACE_EVENTS', payload: mappedEvents });

          if (data.result) {
            dispatch({ type: 'SET_OUTPUT', payload: data.result });
          } else {
            // Look for last observation event (both 'observation_emitted' and 'observation' types)
            const lastObservation = mappedEvents
              .filter((e: any) => e.type === 'observation_emitted' || e.type === 'observation')
              .pop();
            if (lastObservation?.data) {
              // Check for structured output first (from playground runs)
              if (lastObservation.data.output) {
                const output = lastObservation.data.output;
                dispatch({
                  type: 'SET_OUTPUT',
                  payload: typeof output === 'string' ? output : JSON.stringify(output, null, 2)
                });
              } else if (lastObservation.data.content) {
                dispatch({ type: 'SET_OUTPUT', payload: lastObservation.data.content });
              }
            }
          }

          // Check for HITL events - look for pending HITL request
          const hitlRequestEvent = mappedEvents.find(
            (e: any) => e.type === 'hitl_request_created' || e.type === 'hitl_interrupt'
          );
          const hitlResolvedEvent = mappedEvents.find(
            (e: any) =>
              e.type === 'hitl_response_received' ||
              e.type === 'hitl_resumed' ||
              e.type === 'hitl_request_expired' ||
              e.type === 'hitl_request_rejected' ||
              e.type === 'hitl_auto_approved'
          );

          // If there's a HITL request and no resolution, fetch the pending request
          if (hitlRequestEvent && !hitlResolvedEvent && data.status === 'running') {
            try {
              const pendingRequests = await fetchHITLRequests({
                trajectory_id: result.trajectory_id,
                status: 'pending',
                limit: 1,
              });
              if (pendingRequests.length > 0) {
                dispatch({ type: 'SET_PENDING_HITL_REQUEST', payload: pendingRequests[0] });
                dispatch({ type: 'SET_STATUS', payload: 'hitl_pending' });
              }
            } catch (error) {
              console.error('Failed to fetch HITL request:', error);
            }
          } else if (hitlResolvedEvent) {
            // Clear the pending HITL request if it was resolved
            dispatch({ type: 'SET_PENDING_HITL_REQUEST', payload: null });
          }

          // Only update status if we have a definitive status (not 'running' or 'unknown')
          // 'unknown' is transient (e.g., API error or trajectory not yet created) - keep polling
          if (data.status !== 'running' && data.status !== 'unknown') {
            dispatch({ type: 'SET_RUNNING', payload: false });
            dispatch({ type: 'SET_PENDING_HITL_REQUEST', payload: null });
            // Map backend status to IDE status
            // Backend statuses: running, completed, failed, cancelled
            let ideStatus: 'completed' | 'failed' | 'cancelled' = 'failed';
            if (data.status === 'completed' || data.status === 'success') {
              ideStatus = 'completed';
            } else if (data.status === 'cancelled') {
              ideStatus = 'cancelled';
            }
            dispatch({ type: 'SET_STATUS', payload: ideStatus });
            // Clear polling reference since it auto-stops
            pollStopRef.current = null;
          }
        },
        2000,  // Poll every 2 seconds
        true   // Auto-stop on completion
      );
    } catch (error: any) {
      dispatch({ type: 'SET_RUNNING', payload: false });
      dispatch({ type: 'SET_STATUS', payload: 'failed' });
      dispatch({ type: 'SET_RUN_ERROR', payload: error.message || 'Failed to run agent' });
    }
  }, [state.isRunning, state.userPrompt, state.inputVariables, state.promptConfig, state.bottomPanelHeight]);

  const stopExecution = useCallback(async () => {
    if (state.trajectoryId) {
      try {
        await playgroundApi.stop(state.trajectoryId);
      } catch (error) {
        console.error('Failed to stop:', error);
      }
    }
    if (pollStopRef.current) {
      pollStopRef.current();
      pollStopRef.current = null;
    }
    if (chatControllerRef.current) {
      chatControllerRef.current.abort();
    }
    dispatch({ type: 'SET_RUNNING', payload: false });
    dispatch({ type: 'SET_STATUS', payload: 'idle' });
  }, [state.trajectoryId]);

  const sendChatMessage = useCallback((agentId: string, agentContent: string, message: string) => {
    if (state.isRunning) return;

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: message,
    };

    dispatch({ type: 'ADD_MESSAGE', payload: userMessage });
    dispatch({ type: 'SET_RUNNING', payload: true });

    const assistantMessageId = `assistant-${Date.now()}`;
    dispatch({
      type: 'ADD_MESSAGE',
      payload: {
        id: assistantMessageId,
        role: 'assistant',
        content: '',
        isStreaming: true,
      },
    });

    let accumulatedContent = '';

    chatControllerRef.current = playgroundApi.chat(
      {
        agent_id: agentId,
        trajectory_id: state.trajectoryId || undefined,
        messages: [...state.messages, userMessage].map((m) => ({
          role: m.role,
          content: m.content,
        })),
        input_variables: state.inputVariables,
        agent_content: agentContent,
      },
      (data) => {
        if (data.type === 'text') {
          accumulatedContent += data.content;
          dispatch({
            type: 'UPDATE_MESSAGE',
            payload: { id: assistantMessageId, content: accumulatedContent },
          });
        } else if (data.type === 'trajectory_id') {
          dispatch({ type: 'SET_TRAJECTORY_ID', payload: data.trajectory_id });
        }
      },
      (error) => {
        dispatch({ type: 'SET_RUN_ERROR', payload: error.message });
        dispatch({ type: 'SET_RUNNING', payload: false });
      },
      () => {
        dispatch({
          type: 'UPDATE_MESSAGE',
          payload: { id: assistantMessageId, content: accumulatedContent, isStreaming: false },
        });
        dispatch({ type: 'SET_RUNNING', payload: false });
      }
    );
  }, [state.isRunning, state.trajectoryId, state.messages, state.inputVariables]);

  const clearChat = useCallback(() => {
    dispatch({ type: 'SET_MESSAGES', payload: [] });
    dispatch({ type: 'SET_TRAJECTORY_ID', payload: null });
  }, []);

  const setUserPrompt = useCallback((prompt: string) => {
    dispatch({ type: 'SET_USER_PROMPT', payload: prompt });
  }, []);

  const setPromptConfig = useCallback((config: PromptConfig) => {
    dispatch({ type: 'SET_PROMPT_CONFIG', payload: config });
  }, []);

  const setInputVariables = useCallback((variables: Record<string, string>) => {
    dispatch({ type: 'SET_INPUT_VARIABLES', payload: variables });
  }, []);

  const setBottomPanelHeight = useCallback((height: number) => {
    dispatch({ type: 'SET_BOTTOM_PANEL_HEIGHT', payload: height });
  }, []);

  const setBottomPanelTab = useCallback((tab: 'output' | 'logs' | 'trace') => {
    dispatch({ type: 'SET_BOTTOM_PANEL_TAB', payload: tab });
  }, []);

  const setChatPanelOpen = useCallback((open: boolean) => {
    dispatch({ type: 'SET_CHAT_PANEL_OPEN', payload: open });
    // Mutual exclusivity with global assistant
    if (open && isAssistantOpen) {
      closeAssistant();
    }
  }, [isAssistantOpen, closeAssistant]);

  const setChatPanelWidth = useCallback((width: number) => {
    dispatch({ type: 'SET_CHAT_PANEL_WIDTH', payload: width });
  }, []);

  const expandBottomPanel = useCallback(() => {
    if (state.bottomPanelHeight === 0) {
      dispatch({ type: 'SET_BOTTOM_PANEL_HEIGHT', payload: 200 });
    }
  }, [state.bottomPanelHeight]);

  const collapseBottomPanel = useCallback(() => {
    dispatch({ type: 'SET_BOTTOM_PANEL_HEIGHT', payload: 0 });
  }, []);

  // HITL Actions
  const approveHITL = useCallback(
    async (response?: Record<string, unknown>, notes?: string) => {
      if (!state.pendingHITLRequest) return;

      try {
        await respondToHITLRequest(state.pendingHITLRequest.id, {
          response: response || { action: 'approve' },
          notes,
        });
        dispatch({ type: 'SET_PENDING_HITL_REQUEST', payload: null });
        dispatch({ type: 'SET_STATUS', payload: 'running' });
      } catch (error) {
        console.error('Failed to approve HITL request:', error);
        throw error;
      }
    },
    [state.pendingHITLRequest]
  );

  const rejectHITL = useCallback(
    async (reason?: string) => {
      if (!state.pendingHITLRequest) return;

      try {
        await rejectHITLRequest(state.pendingHITLRequest.id, reason);
        dispatch({ type: 'SET_PENDING_HITL_REQUEST', payload: null });
        dispatch({ type: 'SET_STATUS', payload: 'failed' });
        dispatch({ type: 'SET_RUNNING', payload: false });
      } catch (error) {
        console.error('Failed to reject HITL request:', error);
        throw error;
      }
    },
    [state.pendingHITLRequest]
  );

  const dismissHITL = useCallback(() => {
    dispatch({ type: 'SET_PENDING_HITL_REQUEST', payload: null });
  }, []);

  const resetExecution = useCallback(() => {
    dispatch({ type: 'RESET_EXECUTION' });
  }, []);

  // -------------------------------------------------------------------------
  // Context value
  // -------------------------------------------------------------------------

  const value = useMemo<IDEContextValue>(
    () => ({
      ...state,
      setMode,
      runAgent,
      stopExecution,
      sendChatMessage,
      clearChat,
      setUserPrompt,
      setPromptConfig,
      setInputVariables,
      approveHITL,
      rejectHITL,
      dismissHITL,
      setBottomPanelHeight,
      setBottomPanelTab,
      setChatPanelOpen,
      setChatPanelWidth,
      expandBottomPanel,
      collapseBottomPanel,
      resetExecution,
    }),
    [
      state,
      setMode,
      runAgent,
      stopExecution,
      sendChatMessage,
      clearChat,
      setUserPrompt,
      setPromptConfig,
      setInputVariables,
      approveHITL,
      rejectHITL,
      dismissHITL,
      setBottomPanelHeight,
      setBottomPanelTab,
      setChatPanelOpen,
      setChatPanelWidth,
      expandBottomPanel,
      collapseBottomPanel,
      resetExecution,
    ]
  );

  return <IDEContext.Provider value={value}>{children}</IDEContext.Provider>;
}

// ============================================================================
// Hook
// ============================================================================

export function useIDE(): IDEContextValue {
  const context = useContext(IDEContext);
  if (!context) {
    throw new Error('useIDE must be used within an IDEProvider');
  }
  return context;
}

export default IDEContext;
